import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { AuthService } from '@auth/services';
import { ConsigneeService } from '@consignee/services';

@Component({
  selector: 'z-customer-consignee-lookup',
  templateUrl: './customer-consignee-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class CustomerConsigneeLookupComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @Input() custid: string;
  @Input() billTo: string = 'N';
  @Input() shipTo: string = 'N';
  @Input() hideBillToShipToFilter: boolean = false;

  @Input() fieldName: string;

  @Input() label: string;
  @Input() placeholder: string;
  @Input() hint: string;

  @Input() hideLabel: boolean = false;

  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = true;

  @Input() clearButton: boolean = true;
  @Input() manualInput: boolean = true;

  @Input() enableDropdown: boolean = true;
  @Input() showDropdownFilter: boolean = true;
  @Input() doubleClickToOpenPopup: boolean = true;

  @Output() change = new EventEmitter<string>();
  @Output() clear = new EventEmitter<any>();

  useDefaultLabel: boolean = false;
  useDefaultPlaceholder: boolean = false;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private translocoService: TranslocoService,
    private consigneeService: ConsigneeService,
    public formGroup: FormGroupDirective,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.hideLabel && !this.label) {
          this.useDefaultLabel = true;
          this.label = this.translate('core.components.lookups.consignee_lookup.label');
        }

        if (!this.placeholder) {
          this.useDefaultPlaceholder = true;
          this.placeholder = this.translate('core.components.lookups.consignee_lookup.placeholder');
        }
      });

    this.translocoService.events$
      .pipe(
        filter((event) => event.type === 'langChanged'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onLanguageChange();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onLanguageChange() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.hideLabel && this.useDefaultLabel) {
          this.label = this.translate('core.components.lookups.consignee_lookup.label');
        }

        if (this.useDefaultPlaceholder) {
          this.placeholder = this.translate('core.components.lookups.consignee_lookup.placeholder');
        }
      });
  }

  openDialog = () => {
    if (this.billTo === 'N' && this.shipTo === 'N') {
      this.billTo = 'Y';
    }

    if (!this.custid) {
      this.custid = this.authService.custid;
    }

    const lookupDialogRef = this.consigneeService.customerConsigneeLookupDialog(this.custid, {
      billTo: this.billTo,
      shipTo: this.shipTo,
      hideBillToShipToFilter: this.hideBillToShipToFilter,
    });

    lookupDialogRef.onClose.pipe(take(1)).subscribe((result) => {
      if (result !== undefined) {
        this.formGroup.form.get(this.fieldName).setValue(result.consignee);
      }
    });
  };

  onChangeHandler(value: string) {
    this.change.emit(value);
  }

  onClearHandler(_event: any) {
    this.clear.emit();
  }
}
